import PropTypes from "prop-types"
import { useState } from "react"
import { Link } from "react-router-dom"

import Button from "components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import { isTestMode } from "helpers/application"
import { cn } from "styles/utils"

import ProductCard from "./landing-product/product-card"

const LandingTabbedCarousel = ({ element }) => {
  const { data, event_name: eventName, super_filters } = element || {}
  const { classNames, heading, sections, type } = data || {}
  const [tab, setTab] = useState(super_filters?.[0])

  const currentTab = sections ? (sections[tab.key] ?? {}) : {}

  const handleClick = (title, url) => {
    if (eventName && !isTestMode) {
      dataLayer.push({
        clickedUrl: url,
        element: title,
        event: eventName,
      })
    }
  }

  return (
    <div
      className={cn(
        "desktop:py-60 tablet:px-32 widescreen:px-60 px-16 py-32",
        element?.contain && "contained-wide",
        classNames?.blockClassName
      )}
    >
      <div
        className={cn(
          "desktop:flex-row desktop:mb-16 mb-8 flex flex-col items-center justify-between gap-8",
          classNames?.headerClassName
        )}
      >
        {heading && (
          <h2
            className={cn("h2", classNames?.headingClassName)}
            dangerouslySetInnerHTML={{ __html: heading }}
            id={`${element.anchor_name || element.id}-heading`}
          />
        )}
        <div
          className={cn(
            "flex items-center justify-between gap-32",
            classNames?.tabContainerClassName
          )}
        >
          <ul
            {...(heading && {
              "aria-labelledby": `${element.anchor_name || element.id}-heading`,
            })}
            className={cn(
              "flex list-none items-center",
              classNames?.tabListClassName
            )}
            role="tablist"
          >
            {(super_filters || []).map((filter, i) => (
              <li
                className={cn(
                  "relative",
                  i > 0 && "before:px-4 before:text-black before:content-['|']"
                )}
                key={`${element.id}-tab-${filter.key}`}
                role="presentation"
              >
                <Button
                  aria-controls={`${element.anchor_name || element.id}-${tab.key}-tabpanel`}
                  aria-selected={tab.key === filter.key}
                  className={cn(
                    "text-16 desktop:text-18 text-black-print aria-selected:decoration-primary relative font-semibold uppercase decoration-2 underline-offset-4 aria-selected:underline",
                    classNames?.tabClassName
                  )}
                  id={`${element.anchor_name || element.id}-${tab.key}-tab`}
                  onClick={() => setTab(filter)}
                  role="tab"
                  size="md"
                  variant="tertiary"
                >
                  {filter.name}
                </Button>
              </li>
            ))}
          </ul>
          {currentTab.cta && currentTab.url && (
            <Button
              asChild
              className="desktop:inline-flex hidden"
              onClick={() => handleClick(currentTab.cta, currentTab.url)}
              size="md"
              variant="primary"
            >
              <Link
                data-lc={element.anchor_name || element.id}
                to={currentTab.url}
              >
                {currentTab.cta}
              </Link>
            </Button>
          )}
        </div>
      </div>
      {type === "grid" ? (
        <div
          className={cn(
            "tablet:grid-cols-4 tablet:gap-24 grid grid-cols-2 gap-x-12 gap-y-24 px-16",
            classNames?.gridClassName
          )}
        >
          {(currentTab.products || []).map((product, i) => (
            <ProductCard
              element={element}
              handleClick={handleClick}
              imageSizes={
                classNames?.imageSizes ||
                (element?.contain
                  ? "(min-width: 1920px) 424px, (min-width: 768px) 25vw, 50vw"
                  : "(min-width: 768px) 25vw, 50vw")
              }
              index={i}
              key={`${tab.key}-${product.style_code}-${i}`}
              product={product}
            />
          ))}
        </div>
      ) : (
        <Carousel
          aria-labelledby={`${element.anchor_name || element.id}-${tab.key}-tab`}
          className={classNames?.carouselClassName}
          description={`${tab.name} Carousel`}
          id={`${element.anchor_name || element.id}-${tab.key}-tabpanel`}
          opts={{
            active: true,
            breakpoints: {
              "(min-width: 1024px)": {
                active: false,
              },
            },
            dragFree: true,
            loop: false,
            ...classNames?.carouselProps,
          }}
          role="tabpanel"
        >
          <CarouselContent
            className={classNames?.carouselContentClassName}
            viewportClassName={cn(
              "desktop:-ml-32 -mx-16",
              classNames?.viewportClassName
            )}
            viewportProps={classNames?.viewportProps}
            {...classNames?.carouselContentProps}
          >
            {(currentTab.products || []).map((product, i) => (
              <CarouselItem
                className={cn(
                  "desktop:basis-1/4 tablet:basis-3/10 desktop:pl-32 tablet:min-w-0 min-w-166 basis-[45%] pl-16",
                  classNames?.carouselItemClassName
                )}
                key={`${tab.key}-${product.style_code}-${i}`}
                {...classNames?.carouselItemProps}
              >
                <ProductCard
                  element={element}
                  handleClick={handleClick}
                  imageSizes={
                    classNames?.imageSizes ||
                    "(max-width: 1023px) 40vw, (max-width: 1919px) 21vw, 346px"
                  }
                  index={i}
                  product={product}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="tablet:inline-flex" />
          <CarouselNext className="tablet:inline-flex" />
        </Carousel>
      )}
      {currentTab.cta && currentTab.url && (
        <div className="desktop:hidden mt-16 text-center">
          <Button
            asChild
            onClick={() => handleClick(currentTab.cta, currentTab.url)}
            size="md"
            variant="primary"
          >
            <Link
              data-lc={element.anchor_name || element.id}
              to={currentTab.url}
            >
              {currentTab.cta}
            </Link>
          </Button>
        </div>
      )}
    </div>
  )
}

LandingTabbedCarousel.propTypes = {
  element: PropTypes.object,
}

export default LandingTabbedCarousel
